import { cn } from '@/lib/utils';

/**
 * Mazeed's canonical "green clock" loader.
 *
 * Eight brand dots arranged like clock positions; the ring rotates while
 * each dot pulses. This is THE loading indicator for the whole portal —
 * page, section, overlay and button loading states all resolve to this
 * same mark so the experience stays perfectly consistent.
 *
 * Animation keyframes live once in `index.css` (`.mz-loader*`), so nothing
 * is injected per render and `prefers-reduced-motion` is honoured globally.
 * Dots use `currentColor`, so `tone` simply sets a text color.
 */

type LoaderSize = 'xs' | 'sm' | 'md' | 'lg';
type LoaderTone = 'brand' | 'onDark' | 'muted' | 'white' | 'current';

interface ExpressiveLoadingIndicatorProps {
  readonly size?: LoaderSize;
  readonly tone?: LoaderTone;
  readonly text?: string;
  readonly className?: string;
  /** Accessible label announced to screen readers. */
  readonly label?: string;
}

const SIZE_MAP: Record<
  LoaderSize,
  { container: string; dot: string; radius: number; text: string; gap: string }
> = {
  xs: {
    container: 'h-4 w-4',
    dot: 'h-[3px] w-[3px]',
    radius: 5,
    text: 'text-xs',
    gap: 'gap-2',
  },
  sm: {
    container: 'h-9 w-9',
    dot: 'h-1.5 w-1.5',
    radius: 13,
    text: 'text-sm',
    gap: 'gap-3',
  },
  md: {
    container: 'h-16 w-16',
    dot: 'h-[9px] w-[9px]',
    radius: 26,
    text: 'text-base',
    gap: 'gap-5',
  },
  lg: {
    container: 'h-28 w-28',
    dot: 'h-[13px] w-[13px]',
    radius: 46,
    text: 'text-lg',
    gap: 'gap-6',
  },
};

const TONE_MAP: Record<LoaderTone, string> = {
  brand: 'text-[hsl(var(--primary))]',
  // Brand green with a soft halo so it reads on the dark modal backdrop.
  onDark:
    'text-[hsl(var(--primary))] [filter:drop-shadow(0_0_5px_hsl(var(--primary)/0.55))]',
  muted: 'text-muted-foreground',
  white: 'text-white',
  current: 'text-current',
};

export const ExpressiveLoadingIndicator = ({
  size = 'md',
  tone = 'brand',
  text,
  className,
  label,
}: Readonly<ExpressiveLoadingIndicatorProps>) => {
  const s = SIZE_MAP[size];

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className={cn(
        'flex flex-col items-center justify-center',
        s.gap,
        TONE_MAP[tone],
        className
      )}
    >
      <div className={cn('mz-loader relative', s.container)}>
        {Array.from({ length: 8 }).map((_, index) => {
          const angle = (index * 360) / 8;
          const x = Math.cos((angle * Math.PI) / 180) * s.radius;
          const y = Math.sin((angle * Math.PI) / 180) * s.radius;

          return (
            <span
              key={index}
              className={cn('mz-loader-dot', s.dot)}
              style={{
                left: `calc(50% + ${x}px)`,
                top: `calc(50% + ${y}px)`,
              }}
            />
          );
        })}
      </div>

      {text && (
        <p className={cn('font-medium text-muted-foreground', s.text)}>
          {text}
        </p>
      )}

      <span className="sr-only">{label ?? text ?? 'Loading'}</span>
    </div>
  );
};

/**
 * Full-screen / route-level loading. Use for the initial workspace load or
 * any `if (isLoading) return ...` that owns the whole viewport.
 */
export const PageExpressiveLoading = ({
  text = 'Loading...',
  className,
}: Readonly<Pick<ExpressiveLoadingIndicatorProps, 'text' | 'className'>>) => {
  return (
    <div
      className={cn(
        'flex min-h-screen items-center justify-center bg-background',
        className
      )}
    >
      <ExpressiveLoadingIndicator size="lg" text={text} />
    </div>
  );
};

/**
 * Section / panel loading — a centered block inside a populated page
 * (cards, tables, lists). Sits on a min-height so layout doesn't jump.
 */
export const SectionLoading = ({
  text,
  className,
  size = 'md',
}: Readonly<
  Pick<ExpressiveLoadingIndicatorProps, 'text' | 'className' | 'size'>
>) => {
  return (
    <div
      className={cn(
        'flex min-h-[220px] w-full items-center justify-center py-10',
        className
      )}
    >
      <ExpressiveLoadingIndicator size={size} text={text} />
    </div>
  );
};

/**
 * Overlay loading — an absolutely-positioned blurred panel that covers its
 * (relatively-positioned) parent or a dialog while an action runs.
 */
export const OverlayLoading = ({
  message,
  className,
  size = 'md',
}: Readonly<{
  readonly message?: string;
  readonly className?: string;
  readonly size?: LoaderSize;
}>) => {
  return (
    <div
      className={cn(
        'absolute inset-0 z-50 flex items-center justify-center rounded-[inherit] bg-background/75 backdrop-blur-sm animate-in fade-in duration-200',
        className
      )}
    >
      <ExpressiveLoadingIndicator size={size} text={message} />
    </div>
  );
};

/**
 * Inline button spinner. Drop-in replacement for
 * `<Loader2 className="mr-2 h-4 w-4 animate-spin" />` — inherits the
 * button's text color so it works on every button variant.
 */
export const ButtonSpinner = ({
  className,
}: Readonly<{ readonly className?: string }>) => {
  return (
    <ExpressiveLoadingIndicator
      size="xs"
      tone="current"
      className={cn('inline-flex', className)}
      label="Working"
    />
  );
};

export default ExpressiveLoadingIndicator;
